<script setup lang="ts">
import { refDebounced } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEquipmentList } from '@/composables/equipment/useEquipmentList';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import { EquipmentService } from '@/services/equipment.services';
import type { ISite } from '@/types/equipment.types';
import EquipmentConfirmModal from './EquipmentConfirmModal.vue';
import EquipmentField from './EquipmentField.vue';
import EquipmentSelect from './EquipmentSelect.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';
import EquipmentTabShell from './EquipmentTabShell.vue';
import SiteFormModal from './SiteFormModal.vue';

const props = defineProps<{ lockedStoreId?: string }>();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const { isSystemAdmin } = useIsSystemAdmin();

const search = ref('');
// Dans la fiche d'un commerce, le commerce est fixé : ni filtre ni choix.
const storeId = ref(props.lockedStoreId ?? '');
const sort = ref<'name' | 'createdAt'>('name');
const debouncedSearch = refDebounced(search, 300);

const sortOptions = computed(() => [
  { value: 'name' as const, label: t('equipment.sortName') },
  { value: 'createdAt' as const, label: t('equipment.sortRecent') },
]);
const filtersActive = computed(
  () => !!search.value || storeId.value !== (props.lockedStoreId ?? ''),
);
const resetFilters = () => {
  search.value = '';
  storeId.value = props.lockedStoreId ?? '';
};

const list = useEquipmentList<ISite>(
  'sites',
  () => ({
    q: debouncedSearch.value.trim() || undefined,
    storeIds: storeId.value ? [storeId.value] : undefined,
    sortBy: sort.value,
    sortOrder: sort.value === 'name' ? 'ASC' : 'DESC',
  }),
  EquipmentService.searchSites,
);

const creating = defineModel<boolean>('creating', { default: false });
const editing = ref<ISite | null>(null);
const deleting = ref<ISite | null>(null);
const formOpen = computed(() => creating.value || !!editing.value);

const closeForm = () => {
  creating.value = false;
  editing.value = null;
};
const onDeleted = async () => {
  deleting.value = null;
  await queryClient.invalidateQueries({ queryKey: ['equipment'] });
};

const addressOf = (site: ISite) =>
  [
    site.addressLine1,
    site.addressLine2,
    [site.postalCode, site.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ') || '—';
</script>

<template>
  <EquipmentTabShell
    v-model:page="list.page.value"
    v-model:page-size="list.pageSize.value"
    :count="list.rows.value.length"
    :total="list.total.value"
    :page-count="list.pageCount.value"
    :is-loading="list.isLoading.value"
    :is-error="list.isError.value"
    empty-icon="pi-map-marker"
    @retry="list.refetch()"
  >
    <template #toolbar>
      <EquipmentField :label="t('equipment.search')" class="eqs__wide">
        <div class="eq-search">
          <i class="pi pi-search" />
          <input
            v-model="search"
            class="eq-input"
            type="search"
            :placeholder="t('equipment.sites.searchPlaceholder')"
          />
        </div>
      </EquipmentField>
      <EquipmentField
        v-if="!lockedStoreId"
        :label="t('equipment.fields.store')"
      >
        <EquipmentStoreSelect v-model="storeId" allow-all />
      </EquipmentField>
      <EquipmentField :label="t('equipment.sort')">
        <EquipmentSelect
          v-model="sort"
          :options="sortOptions"
          :search-above="0"
          icon="pi-sort-alt"
        />
      </EquipmentField>
      <button
        v-if="filtersActive"
        type="button"
        class="eq-btn eq-btn--ghost"
        @click="resetFilters"
      >
        <i class="pi pi-filter-slash" /> {{ t('equipment.reset') }}
      </button>
    </template>

    <table class="eq-table">
      <thead>
        <tr>
          <th>{{ t('equipment.fields.siteName') }}</th>
          <th>{{ t('equipment.fields.store') }}</th>
          <th>{{ t('equipment.fields.country') }}</th>
          <th>{{ t('equipment.fields.address') }}</th>
          <th v-if="isSystemAdmin" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="site in list.rows.value" :key="site.id">
          <td>
            <div class="eq-cell">
              <span class="eq-chip"><i class="pi pi-map-marker" /></span>
              <strong>{{ site.name }}</strong>
            </div>
          </td>
          <td>{{ site.store.name }}</td>
          <td>
            <span class="eq-badge eq-badge--soft">{{ site.country }}</span>
          </td>
          <td class="eq-muted">{{ addressOf(site) }}</td>
          <td v-if="isSystemAdmin">
            <div class="eq-actions">
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.edit')"
                @click="editing = site"
              >
                <i class="pi pi-pencil" />
              </button>
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.delete')"
                @click="deleting = site"
              >
                <i class="pi pi-trash" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </EquipmentTabShell>

  <SiteFormModal
    v-if="formOpen"
    :site="editing ?? undefined"
    :locked-store-id="lockedStoreId"
    @close="closeForm"
    @saved="closeForm"
  />
  <EquipmentConfirmModal
    v-if="deleting"
    :title="t('equipment.sites.delete')"
    :message="t('equipment.sites.deleteMessage', { name: deleting.name })"
    :run="() => EquipmentService.deleteSite(deleting!.store.id, deleting!.id)"
    @close="deleting = null"
    @done="onDeleted"
  />
</template>
